import type { Session } from '@supabase/supabase-js';
import { supabase } from './supabase';

/**
 * The signup page on frameon.us cannot share a session with this origin,
 * so it passes the tokens in the URL fragment. Take them, then scrub the
 * address bar — a URL left holding a refresh token ends up in browser history
 * and in whatever the next page reads as a referrer.
 */
export async function consumeHandoff(): Promise<Session | null> {
  const raw = window.location.hash.slice(1);
  if (!raw) return null;

  const params = new URLSearchParams(raw);
  const accessToken = params.get('access_token');
  const refreshToken = params.get('refresh_token');
  const oauthError = params.get('error_description') || params.get('error');

  window.history.replaceState(null, '', window.location.pathname + window.location.search);

  if (oauthError) throw new Error(oauthError);
  if (!accessToken || !refreshToken) return null;

  const { data, error } = await supabase().auth.setSession({
    access_token: accessToken,
    refresh_token: refreshToken
  });
  if (error) throw new Error(error.message);
  return data.session;
}

/**
 * An emailed link carries a one-time token instead of a session. Supabase
 * verifies it and hands back a real session.
 */
export async function verifyEmailToken(tokenHash: string, type: string): Promise<Session | null> {
  const { data, error } = await supabase().auth.verifyOtp({
    token_hash: tokenHash,
    type: type as 'magiclink' | 'signup' | 'invite' | 'recovery' | 'email_change'
  });
  if (error) throw new Error(error.message);
  return data.session;
}
